import { FormControl, FormHelperText, FormLabel, ToggleButtonGroup, ToggleButtonGroupProps } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useField } from 'formik';
import React, { useCallback } from 'react';
import { useHelperTextClasses } from '../BaseTextField';

export type ButtonStyleRadioGroupProps = ToggleButtonGroupProps & {
  name: string;
  label: string;
  error?: boolean;
  helperText?: string;
  color?: 'primary' | 'secondary';
  fullWidth?: boolean;
  handleChange?: (name: string, value: string | null) => void;
};

export const ButtonStyleRadioGroup: React.FC<ButtonStyleRadioGroupProps> = ({
  name,
  label,
  orientation = 'vertical',
  handleChange: propsHandleChange,
  ...props
}) => {
  const [ field, meta ] = useField({
    name,
    multiple: false,
  });

  const { onChange } = field;
  const { classes: helperTextClasses } = useHelperTextClasses({});
  const { classes: buttonStyles, cx } = useButtonStyleRadioGroupStyles();
  const hasError = Boolean(props.error && props.helperText) || Boolean(meta.error && meta.touched);
  const helperText = props.helperText || (meta.touched && meta.error);

  const handleChange = useCallback((e: React.MouseEvent<HTMLElement, MouseEvent>, value: string | null) => {
    onChange({
      target: {
        name,
        value,
      },
    });

    propsHandleChange?.(name, value);
  }, [ onChange, name, propsHandleChange ]);

  return (
    <FormControl component="fieldset" error={hasError} className={cx(props.fullWidth && buttonStyles.fullWidth, props.className)}>
      <FormLabel component="legend">{label}</FormLabel>
      <ToggleButtonGroup
        {...props}
        exclusive
        orientation={orientation}
        aria-label={name}
        value={field.value}
        onChange={handleChange}
        color={props.color}
        className={buttonStyles.radioStyle}
      >
        {props.children}
      </ToggleButtonGroup>
      {helperText && (
        <FormHelperText className={helperTextClasses.root}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

const useButtonStyleRadioGroupStyles = makeStyles({ name: 'ButtonStyleRadioGroup' })(theme => ({
  radioStyle: {
    marginTop: theme.spacing(2),
  },
  fullWidth: {
    width: '100%',
  }
}));
